/**
 * System Configuration React Query Hooks
 * 
 * Provides React Query hooks for system configuration operations with caching,
 * optimistic updates, and automatic refetching.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { 
  systemConfigService, 
  type UpsertSystemConfigInput 
} from '../services/SystemConfigService';
import type { SystemConfig } from '../repositories/SystemConfigRepository';

/**
 * Query keys for system config queries
 */
export const systemConfigKeys = {
  all: ['systemConfig'] as const,
  byCompany: (companyId: string) => [...systemConfigKeys.all, companyId] as const,
};

/**
 * Hook to fetch system config for a company
 */
export function useSystemConfig(
  companyId: string,
  options?: UseQueryOptions<SystemConfig | null, Error>
) {
  return useQuery<SystemConfig | null, Error>({
    queryKey: systemConfigKeys.byCompany(companyId),
    queryFn: () => systemConfigService.getConfig(companyId),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to upsert system config
 */
export function useUpsertSystemConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, input }: { companyId: string; input: UpsertSystemConfigInput }) => 
      systemConfigService.upsertConfig(companyId, input),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        systemConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to toggle ACD (Automatic Call Distribution)
 */
export function useToggleACD() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) => systemConfigService.toggleACD(companyId),
    onMutate: async (companyId) => {
      await queryClient.cancelQueries({ queryKey: systemConfigKeys.byCompany(companyId) });
      
      const previousConfig = queryClient.getQueryData<SystemConfig>(
        systemConfigKeys.byCompany(companyId)
      );
      
      if (previousConfig) {
        queryClient.setQueryData<SystemConfig>(
          systemConfigKeys.byCompany(companyId),
          {
            ...previousConfig,
            auto_call_distribution: !previousConfig.auto_call_distribution,
          }
        );
      }
      
      return { previousConfig };
    },
    onError: (err, companyId, context) => {
      if (context?.previousConfig) {
        queryClient.setQueryData(
          systemConfigKeys.byCompany(companyId),
          context.previousConfig
        );
      }
    },
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        systemConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to update ring strategy
 */
export function useUpdateRingStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, strategy }: { companyId: string; strategy: 'round_robin' | 'least_recent' | 'skill_based' | 'priority' }) =>
      systemConfigService.updateRingStrategy(companyId, strategy),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        systemConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to update enabled features
 */
export function useUpdateFeatures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, features }: { 
      companyId: string; 
      features: Partial<Pick<SystemConfig, 'voicemail_enabled' | 'callback_enabled' | 'quality_monitoring' | 'ai_assistance' | 'real_time_transcription' | 'sentiment_analysis'>>
    }) =>
      systemConfigService.updateFeatures(companyId, features),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        systemConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to update performance targets
 */
export function useUpdateTargets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, targets }: { 
      companyId: string; 
      targets: Partial<Pick<SystemConfig, 'target_answer_rate' | 'target_quality_score' | 'max_avg_wait_time'>>
    }) =>
      systemConfigService.updateTargets(companyId, targets),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        systemConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}

/**
 * Hook to update max ring time
 */
export function useUpdateMaxRingTime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, seconds }: { companyId: string; seconds: number }) =>
      systemConfigService.updateMaxRingTime(companyId, seconds),
    onSuccess: (updatedConfig) => {
      queryClient.setQueryData(
        systemConfigKeys.byCompany(updatedConfig.company_id),
        updatedConfig
      );
    },
  });
}
